import { USER_ROLE } from './user-role'

export enum FEATURE {
  /** Meyreal features (Operator Meyreal owns CRUD) */
  PTO = 'PTO',
  PTO_REWARDS = 'PTO_REWARDS',
  PROPERTY_ACTIVITIES = 'PROPERTY_ACTIVITIES',
  PTO_REFERRAL = 'PTO_REFERRAL',
  BOOSTER_PROGRAM = 'BOOSTER_PROGRAM',
  PROMO_CODE = 'PROMO_CODE',
  DAO = 'DAO',
  STAKING_NFT = 'STAKING_NFT',
  USER_MEY_REAL = 'USER_MEY_REAL',
  BANNER = 'BANNER',
  WHATS_NEW_VIEW = 'WHATS_NEW_VIEW',
  WHATS_NEW_CREATE = 'WHATS_NEW_CREATE',
  FAQ_VIEW = 'FAQ_VIEW',
  FAQ_CREATE = 'FAQ_CREATE',

  /** Meyfi features (Operator Meyfi owns CRUD) */
  VESTING = 'VESTING',
  ACCOUNT_VESTING = 'ACCOUNT_VESTING',
  STAKING_TOKEN = 'STAKING_TOKEN',
  AFFILIATE = 'AFFILIATE',
  USER_MEY_FI = 'USER_MEY_FI',

  /** Super-admin-only features (Admin role can view) */
  P2P_LENDING = 'P2P_LENDING',
  AIRDROP = 'AIRDROP',
  EMAIL = 'EMAIL',
  MARKETPLACE = 'MARKETPLACE',
  TRANSACTION_HISTORY = 'TRANSACTION_HISTORY',
  STAKING_POOL = 'STAKING_POOL',
  PORTAL_TOKENIZE = 'PORTAL_TOKENIZE',

  /** Shared operational features (both Operators + Super Admin can CRUD; Admin views) */
  SETTING = 'SETTING',

  /** Admin management (Creator + Super Admin) */
  ADMIN_MANAGEMENT = 'ADMIN_MANAGEMENT',

  /** Universal — every authenticated admin */
  DASHBOARD = 'DASHBOARD',
}

type FeatureCategory =
  | 'MEYREAL'
  | 'MEYFI'
  | 'SHARED'
  | 'EMAIL'
  | 'SHARED_OPERATIONS'
  | 'ADMIN_MGMT'
  | 'UNIVERSAL'

const FEATURE_CATEGORIES: Record<FEATURE, FeatureCategory> = {
  [FEATURE.PTO]: 'MEYREAL',
  [FEATURE.PTO_REWARDS]: 'MEYREAL',
  [FEATURE.PROPERTY_ACTIVITIES]: 'MEYREAL',
  [FEATURE.PTO_REFERRAL]: 'MEYREAL',
  [FEATURE.BOOSTER_PROGRAM]: 'MEYREAL',
  [FEATURE.PROMO_CODE]: 'MEYREAL',
  [FEATURE.DAO]: 'MEYREAL',
  [FEATURE.STAKING_NFT]: 'MEYREAL',
  [FEATURE.USER_MEY_REAL]: 'MEYREAL',
  [FEATURE.BANNER]: 'MEYREAL',
  [FEATURE.WHATS_NEW_VIEW]: 'MEYREAL',
  [FEATURE.WHATS_NEW_CREATE]: 'MEYREAL',
  [FEATURE.FAQ_VIEW]: 'MEYREAL',
  [FEATURE.FAQ_CREATE]: 'MEYREAL',

  [FEATURE.VESTING]: 'MEYFI',
  [FEATURE.ACCOUNT_VESTING]: 'MEYFI',
  [FEATURE.STAKING_TOKEN]: 'MEYFI',
  [FEATURE.AFFILIATE]: 'MEYFI',
  [FEATURE.USER_MEY_FI]: 'MEYFI',

  [FEATURE.P2P_LENDING]: 'SHARED',
  [FEATURE.AIRDROP]: 'SHARED',
  [FEATURE.EMAIL]: 'EMAIL',
  [FEATURE.MARKETPLACE]: 'SHARED',
  [FEATURE.TRANSACTION_HISTORY]: 'SHARED',
  [FEATURE.STAKING_POOL]: 'SHARED',
  [FEATURE.PORTAL_TOKENIZE]: 'SHARED',
  [FEATURE.SETTING]: 'SHARED_OPERATIONS',

  [FEATURE.ADMIN_MANAGEMENT]: 'ADMIN_MGMT',

  [FEATURE.DASHBOARD]: 'UNIVERSAL',
}

const VIEW_ROLES_BY_CATEGORY: Record<FeatureCategory, USER_ROLE[]> = {
  MEYREAL: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.OPERATOR_MEYREAL],
  MEYFI: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.OPERATOR_MEYFI],
  SHARED: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN],
  EMAIL: [USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.OPERATOR_MEYFI],
  SHARED_OPERATIONS: [
    USER_ROLE.SUPER_ADMIN,
    USER_ROLE.ADMIN,
    USER_ROLE.OPERATOR_MEYREAL,
    USER_ROLE.OPERATOR_MEYFI,
  ],
  ADMIN_MGMT: [USER_ROLE.SUPER_ADMIN, USER_ROLE.CREATOR],
  UNIVERSAL: [
    USER_ROLE.SUPER_ADMIN,
    USER_ROLE.ADMIN,
    USER_ROLE.CREATOR,
    USER_ROLE.OPERATOR_MEYREAL,
    USER_ROLE.OPERATOR_MEYFI,
  ],
}

const CRUD_ROLES_BY_CATEGORY: Record<FeatureCategory, USER_ROLE[]> = {
  MEYREAL: [USER_ROLE.SUPER_ADMIN, USER_ROLE.OPERATOR_MEYREAL],
  MEYFI: [USER_ROLE.SUPER_ADMIN, USER_ROLE.OPERATOR_MEYFI],
  SHARED: [USER_ROLE.SUPER_ADMIN],
  EMAIL: [USER_ROLE.SUPER_ADMIN],
  SHARED_OPERATIONS: [USER_ROLE.SUPER_ADMIN, USER_ROLE.OPERATOR_MEYREAL, USER_ROLE.OPERATOR_MEYFI],
  ADMIN_MGMT: [USER_ROLE.SUPER_ADMIN, USER_ROLE.CREATOR],
  UNIVERSAL: [
    USER_ROLE.SUPER_ADMIN,
    USER_ROLE.ADMIN,
    USER_ROLE.CREATOR,
    USER_ROLE.OPERATOR_MEYREAL,
    USER_ROLE.OPERATOR_MEYFI,
  ],
}

export const getViewRoles = (feature: FEATURE): USER_ROLE[] =>
  VIEW_ROLES_BY_CATEGORY[FEATURE_CATEGORIES[feature]]

export const getCrudRoles = (feature: FEATURE): USER_ROLE[] =>
  CRUD_ROLES_BY_CATEGORY[FEATURE_CATEGORIES[feature]]
